import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { Address } from '../model/address';
import { Wayforpay, WayforpayResponse } from '../model/wayforpay';
import { ApiService } from './api.service';

/** Their widget, which takes the card details itself so that this shop never sees them. */
const WIDGET = 'https://secure.wayforpay.com/server/pay-widget.js';

/** What the widget posts to the window when the visitor shuts it. There is no callback for it. */
const CLOSED = 'WfpWidgetEventClose';

/** The visitor shut the widget: nothing failed, so there is no failure to report. */
export class PaymentClosed extends Error {

  constructor() {
    super('The payment window was closed');
  }

}

interface WayforpayWidget {
  run(
    payment: Wayforpay,
    approved: (response: WayforpayResponse) => void,
    declined: (response: WayforpayResponse) => void,
    pending: (response: WayforpayResponse) => void
  ): void;
}

declare global {
  interface Window {
    Wayforpay: new () => WayforpayWidget;
  }
}

/**
 * Paying by card, which this application does not do: it only asks unimarket-api to sign the
 * order, and hands that to Wayforpay's widget. The card number is typed into their frame
 * and never reaches us, which is the whole reason to use the widget rather than a form of ours.
 *
 * The script is fetched when someone actually pays, not when the checkout renders. It is a
 * third-party script on every other visitor's critical path otherwise, and most visitors here
 * choose cash on delivery.
 */
@Injectable({ providedIn: 'root' })
export class WayforpayService {

  private api = inject(ApiService);
  private widget?: Promise<void>;

  /**
   * What the widget reports for the order `code`, once it says the card is approved or the bank
   * has taken it for processing - for the API to check with Wayforpay. Rejects when it is declined
   * or the visitor closes the widget, so the caller can give the form back.
   */
  async pay(orderCode: string, address: Address): Promise<WayforpayResponse> {
    const payment = await firstValueFrom(
      this.api.get<Wayforpay>('/orders/' + encodeURIComponent(orderCode) + '/payment'));
    Object.assign(payment, {
      clientFirstName: address.firstName,
      clientLastName: address.lastName,
      clientPhone: address.phone,
      clientCity: address.city,
      clientAddress: address.street,
      language: 'UA'
    });
    await this.load();
    return new Promise<WayforpayResponse>((resolve, reject) => {
      // Approved, declined and pending arrive as callbacks; closing the widget arrives only as
      // a message. Without it the promise never settles and the checkout button stays dead for
      // the rest of the page's life.
      const settled = new AbortController();
      window.addEventListener('message', event => {
        if (event.origin === new URL(WIDGET).origin && event.data === CLOSED) {
          settled.abort();
          reject(new PaymentClosed());
        }
      }, { signal: settled.signal });
      new window.Wayforpay().run(
        payment,
        response => {
          settled.abort();
          resolve(response);
        },
        () => {
          settled.abort();
          reject(new Error('The card was declined'));
        },
        // Pending is not a failure: the bank has the money and the order should be placed.
        response => {
          settled.abort();
          resolve(response);
        }
      );
    });
  }

  /** Once per page, however many times somebody retries a declined card. */
  private load(): Promise<void> {
    if (!this.widget) {
      this.widget = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = WIDGET;
        script.onload = () => resolve();
        script.onerror = () => {
          this.widget = undefined;
          reject(new Error('Cannot load the Wayforpay widget'));
        };
        document.body.appendChild(script);
      });
    }
    return this.widget;
  }

}
